import { Component, ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { ReportEditingProvider } from './primitives';
import { LoadedReportModule, evaluateReportRuntime } from './runtime';
import { CompiledReportBundle } from '@/reports/bundles/types';
import { RunReportContext } from '@/reports/types';

/**
 * A compiled draft's document drawn offscreen, read-only, exactly as it
 * prints, for whatever reads it back: the studio's assistant (as an outline),
 * the SDK kit's render (as HTML), and the checks a fixture makes on the
 * printed document. Works in the browser and under jsdom alike.
 */

const modules = new Map<string, LoadedReportModule>();
/** the compiles kept run: every save makes one, and only the latest few are still drawn */
const KEPT_MODULES = 4;

/** The draft's scripts, run once per compile. */
export function moduleFor(compiled: CompiledReportBundle): LoadedReportModule {
  const cached = modules.get(compiled.hash);
  if (cached) {
    // most recently used last, so the oldest is the first let go
    modules.delete(compiled.hash);
    modules.set(compiled.hash, cached);
    return cached;
  }
  const module = evaluateReportRuntime({ bundleVersionId: `draft:${compiled.hash}`, key: compiled.key, contractVersion: compiled.contractVersion, definitionJs: compiled.definitionJs, documentJs: compiled.documentJs, hash: compiled.hash });
  modules.set(compiled.hash, module);
  while (modules.size > KEPT_MODULES) modules.delete(modules.keys().next().value as string);
  return module;
}

class Catch extends Component<{ onError: (message: string) => void; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error: unknown) { this.props.onError(error instanceof Error ? error.message : String(error)); }
  render() { return this.state.failed ? null : this.props.children; }
}

/** Draw the document, let what it draws settle, and hand the drawn element to `read` before taking it down again. */
export async function drawDocument<T>(module: LoadedReportModule, values: Record<string, unknown>, model: unknown, context: RunReportContext, read: (host: HTMLElement) => T): Promise<{ value: T | null; error: string | null }> {
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.style.cssText = 'position:absolute;left:-10000px;top:0;width:900px;';
  document.body.appendChild(host);
  const root = createRoot(host);
  let error: string | null = null;
  const { Document } = module.components;
  try {
    flushSync(() => root.render(
      <Catch onError={(message) => { error = message; }}>
        <ReportEditingProvider readOnly>
          <Document values={values} model={model as never} context={context} meta={{ reportVersion: module.definition.version }} />
        </ReportEditingProvider>
      </Catch>,
    ));
    await new Promise((resolve) => { setTimeout(resolve, 30); });
    return { value: error === null ? read(host) : null, error };
  } finally {
    root.unmount();
    host.remove();
  }
}
